'use client';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import React, { useEffect, useState } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AutoplayOptions } from 'swiper/types';

import { SliderArrow } from '@/components/SliderArrow';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';

const ImageSlider = ({
  children,
  className,
  pagerClass = '-mt-8',
  autoplay = false,
}: {
  children: React.ReactNode;
  className?: string;
  autoplay?: boolean | AutoplayOptions;
  pagerClass?: string;
}) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) {
      containerRef?.current?.swiper?.autoplay.start();
      setIsMounted(true);
    } else {
      containerRef?.current?.swiper?.autoplay.stop();
    }
  }, [isVisible, containerRef]);

  const addLazy = () => {
    const images = document.querySelectorAll('.homepage-slider-content img');
    images.forEach((image) => {
      image.setAttribute('loading', 'lazy');
    });
  };

  useEffect(() => {
    addLazy();
  }, [isMounted]);

  const swiperInternalComponent = (children: any[]) => {
    return (
      <Swiper
        key={isMounted.toString()}
        ref={containerRef}
        slidesPerView={1}
        spaceBetween={4}
        autoplay={autoplay}
        modules={[Navigation, Autoplay]}
        navigation={
          children?.length > 1
            ? {
                prevEl: '.image-swipe-prev',
                nextEl: '.image-swipe-next',
              }
            : false
        }
        loop
        className="text-xl font-medium"
        wrapperClass="flex"
      >
        {children.map((child, index) => {
          return <SwiperSlide key={`_${child.key}_${index}_`}>{child}</SwiperSlide>;
        })}
      </Swiper>
    );
  };

  return Array.isArray(children) ? (
    <div className={`${className ?? ''} relative`}>
      {isMounted ? swiperInternalComponent(children) : swiperInternalComponent(children && children?.length > 1 ? children?.slice(0, 1) : children)}

      {children?.length > 1 && (
        <>
          <div className="image-swipe-prev swipe-prev absolute top-0 right-0 bottom-0 left-0">
            <SliderArrow className="left-3 sm:left-10 lg:left-36">
              <IconArrowLeftDouble className="h-8 w-8" />
            </SliderArrow>
          </div>
          <div className="image-swipe-next swipe-next absolute top-0 right-0 bottom-0 left-0">
            <SliderArrow className="right-3 sm:right-10 lg:right-36">
              <IconArrowRightDouble className="h-8 w-8" />
            </SliderArrow>
          </div>
        </>
      )}
    </div>
  ) : (
    <></>
  );
};

export default ImageSlider;
